'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { checkRateLimit, recordFailedAttempt, resetRateLimit } from '@/lib/rateLimiter';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState<{ attemptsLeft: number; lockedUntil: number | null } | null>(null);
  const { signIn, isAuthenticated, loading: authLoading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      router.replace('/admin');
    }
  }, [isAuthenticated, authLoading, router]);

  // Check rate limit on mount
  useEffect(() => {
    const key = 'login_attempts';
    const result = checkRateLimit(key);
    if (!result.allowed) {
      setRateLimitInfo({ attemptsLeft: 0, lockedUntil: result.lockedUntil });
      setError(result.message || 'Too many attempts. Please wait.');
    }
  }, []);

  // Countdown timer for lockout
  useEffect(() => {
    if (!rateLimitInfo?.lockedUntil) return;
    const interval = setInterval(() => {
      const now = Date.now();
      if (rateLimitInfo.lockedUntil && now >= rateLimitInfo.lockedUntil) {
        setRateLimitInfo(null);
        setError('');
        resetRateLimit('login_attempts');
        clearInterval(interval);
      } else {
        const secondsLeft = Math.ceil(((rateLimitInfo.lockedUntil ?? 0) - now) / 1000);
        setError(`Too many failed attempts. Try again in ${Math.ceil(secondsLeft / 60)} minute(s).`);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [rateLimitInfo?.lockedUntil]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const key = 'login_attempts';

    // Check rate limit before attempting login
    const limitCheck = checkRateLimit(key);
    if (!limitCheck.allowed) {
      setRateLimitInfo({ attemptsLeft: 0, lockedUntil: limitCheck.lockedUntil });
      setError(limitCheck.message || 'Too many attempts. Please wait.');
      return;
    }

    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (!password) {
      setError('Password is required');
      return;
    }

    setLoading(true);
    try {
      await signIn(email.trim(), password);
      resetRateLimit(key);
      router.replace('/admin');
    } catch (err: any) {
      const result = recordFailedAttempt(key);
      if (!result.allowed) {
        setRateLimitInfo({ attemptsLeft: 0, lockedUntil: result.lockedUntil });
        setError(result.message || 'Too many attempts. Please wait.');
      } else {
        setRateLimitInfo({ attemptsLeft: result.attemptsLeft, lockedUntil: null });
        const attemptsMsg = result.attemptsLeft > 0 ? ` (${result.attemptsLeft} attempt${result.attemptsLeft !== 1 ? 's' : ''} left)` : '';
        setError((err.message || 'Login failed. Please try again.') + attemptsMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const isLocked = rateLimitInfo?.lockedUntil != null && Date.now() < rateLimitInfo.lockedUntil;

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-4 border-terracotta border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-terracotta mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Login</h1>
          <p className="text-sm text-gray-500 mt-1">Sign in to access the admin panel</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <form onSubmit={handleSubmit} noValidate>
            {/* Error Message */}
            {error && (
              <div className={`mb-5 flex items-start gap-3 border rounded-lg px-4 py-3 text-sm ${isLocked ? 'bg-orange-50 border-orange-300 text-orange-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {/* Email Field */}
            <div className="mb-5">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                </div>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@example.com"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-transparent transition-colors"
                  disabled={loading || isLocked}
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="mb-6">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-transparent transition-colors"
                  disabled={loading || isLocked}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || isLocked}
              className="w-full py-3 px-4 bg-terracotta hover:bg-terracotta/90 disabled:bg-terracotta/60 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Signing in...
                </>
              ) : isLocked ? (
                'Account Temporarily Locked'
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>

        {/* Back to site */}
        <div className="text-center mt-6">
          <a href="/homepage" className="text-sm text-gray-500 hover:text-terracotta transition-colors">
            ← Back to website
          </a>
        </div>
      </div>
    </div>
  );
}
